import { useEffect, useState } from "react";
import { Pencil, Plus, Trash2, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import { AccessDialog } from "@/components/admin/AccessDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Panel, Empty } from "@/components/PageHeader";
import { api, errMsg, fdatetime } from "@/lib/api";
import { PasswordStrength } from "@/components/PasswordStrength";

const UserDialog = ({ user, profiles, onClose, onSaved }) => {
  const [f, setF] = useState(user);
  useEffect(() => setF(user), [user]);
  if (!f) return null;
  const save = async () => {
    try {
      const body = { ...f, password: f.password || null };
      const { data } = f.id ? await api.put(`/admin/users/${f.id}`, body) : await api.post("/admin/users", body);
      toast.success("Utente salvato"); onSaved(data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg bg-white" data-testid="user-dialog">
        <DialogHeader><DialogTitle>{f.id ? "Modifica utente" : "Nuovo utente"}</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <label className="text-xs text-slate-500">Email<Input className="mt-1" type="email" data-testid="user-email-input" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></label>
          <label className="text-xs text-slate-500">Nome e cognome<Input className="mt-1" data-testid="user-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></label>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-xs text-slate-500">Ruolo
              <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
                <SelectTrigger className="mt-1" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="user">Utente</SelectItem><SelectItem value="superadmin">SuperAdmin</SelectItem></SelectContent>
              </Select></label>
            {f.role === "user" && <label className="text-xs text-slate-500">Profilo di accesso
              <Select value={f.profile_id || undefined} onValueChange={(v) => setF({ ...f, profile_id: v })}>
                <SelectTrigger className="mt-1" data-testid="user-profile-select"><SelectValue placeholder="Scegli profilo" /></SelectTrigger>
                <SelectContent className="bg-white">{profiles.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select></label>}
          </div>
          <label className="text-xs text-slate-500">{f.id ? "Nuova password (lascia vuoto per non cambiarla)" : "Password iniziale (min 8 caratteri)"}
            <Input className="mt-1" type="password" autoComplete="new-password" data-testid="user-password-input" value={f.password || ""} onChange={(e) => setF({ ...f, password: e.target.value })} /><PasswordStrength value={f.password} /></label>
          <label className="flex items-center gap-2 text-sm text-slate-700"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="user-active-switch" />Account attivo</label>
        </div>
        <DialogFooter><Button variant="ghost" onClick={onClose}>Annulla</Button><Button data-testid="user-save-button" className="bg-blue-600 hover:bg-blue-700" onClick={save}>Salva utente</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const MODE_LABEL = { purchases: "acquisti", issued: "carte emesse", both: "acquisti + carte" };
const accessLabel = (a) => {
  if (!a) return "profilo";
  const parts = [a.custom ? `personalizzato · ${a.stores?.length ? `${a.stores.length} PdV` : "tutti i PdV"} · ${MODE_LABEL[a.store_mode] || "acquisti"}` : "profilo"];
  const off = ["email", "sms", "whatsapp"].filter((k) => a.channels?.[k] === false);
  if (off.length) parts.push(`no ${off.join("/")}`);
  if (a.monthly_cap) parts.push(`max ${a.monthly_cap}/mese`);
  return parts.join(" · ");
};

export const UsersTab = () => {
  const [rows, setRows] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [edit, setEdit] = useState(null);
  const [acc, setAcc] = useState(null);
  const load = () => api.get("/admin/users").then((r) => setRows(r.data));
  useEffect(() => { load(); api.get("/admin/profiles").then((r) => setProfiles(r.data)); }, []);
  const pname = Object.fromEntries(profiles.map((p) => [p.id, p.name]));
  const del = async (u) => {
    if (!window.confirm(`Eliminare l'utente ${u.email}?`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("Utente eliminato"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Panel title="Utenti" testId="users-panel"
      action={<Button size="sm" data-testid="user-new-button" className="bg-blue-600 hover:bg-blue-700" onClick={() => setEdit({ email: "", name: "", role: "user", profile_id: "", active: true, password: "" })}><Plus className="mr-1.5 h-4 w-4" />Nuovo utente</Button>}>
      {rows && !rows.length && <Empty text="Nessun utente" />}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs text-slate-500"><tr>{["Utente", "Ruolo / profilo", "Accesso", "Stato", "Ultimo accesso", ""].map((h) => <th key={h} className="px-5 py-2 font-medium">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-slate-100">
            {(rows || []).map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`}>
                <td className="px-5 py-3"><div className="font-medium text-slate-800">{u.name || "—"}</div><div className="text-xs text-slate-500">{u.email}</div></td>
                <td className="px-5 py-3 text-xs">{u.role === "superadmin" ? <span className="rounded bg-violet-50 px-1.5 py-0.5 text-violet-700">SuperAdmin</span> : pname[u.profile_id] || "—"}</td>
                <td className="px-5 py-3 text-xs text-slate-500" data-testid={`user-access-${u.id}`}>{u.role === "superadmin" ? "completo" : accessLabel(u.access)}</td>
                <td className="px-5 py-3 text-xs">{u.active ? <span className="text-emerald-600">attivo</span> : <span className="text-slate-400">disattivato</span>}</td>
                <td className="px-5 py-3 text-xs text-slate-500">{fdatetime(u.last_login)}</td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  {u.role !== "superadmin" && <><Button size="sm" variant="outline" data-testid={`user-access-btn-${u.id}`} title="Personalizza accesso" onClick={() => setAcc(u)}><SlidersHorizontal className="h-3.5 w-3.5" /></Button>{" "}</>}
                  <Button size="sm" variant="outline" data-testid={`user-edit-${u.id}`} onClick={() => setEdit({ ...u, password: "" })}><Pencil className="h-3.5 w-3.5" /></Button>{" "}
                  <Button size="sm" variant="outline" data-testid={`user-delete-${u.id}`} onClick={() => del(u)}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <UserDialog user={edit} profiles={profiles} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />
      <AccessDialog user={acc} profiles={profiles} onClose={() => setAcc(null)} onSaved={() => { setAcc(null); load(); }} />
    </Panel>
  );
};
